import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { PdfToAudioDemoPage } from '@/components/astro/new-design-25/SEO/pdf-to-audio-demo-page';
import { hreflangLinks, seo } from '@/seo/seo';

const heroTitle = 'Italian PDF to audio';
const heroDescription =
  'Drop an Italian PDF and hear it read aloud with natural Italian voices. Designed for academic content, art history, literature, and language learning, with proper pronunciation of double consonants and Italian academic vocabulary.';

const featureFeatures = [
  {
    title: 'Natural Italian Voices',
    description:
      'Voices trained on real Italian speech with correct intonation and rhythm, not Spanish or Latin TTS pretending to be Italian.',
  },
  {
    title: 'Doubles and Accents Handled',
    description:
      'Italian double consonants (pp, tt, ll, ss) and stressed vowels are pronounced correctly, which most free PDF readers get wrong.',
  },
  {
    title: 'Academic and Literary Vocabulary',
    description:
      'Handles Italian academic prose, art history, philosophy, and classical literature with proper register.',
  },
  {
    title: 'Right for Language Learners',
    description:
      'Slow the playback for shadowing, then speed up as your Italian ear improves on real native content.',
  },
];

const solutionBenefitsData = {
  title: 'Why Italian PDF listeners stay with this tool',
  items: [
    {
      text: 'Natural Italian voices with the right musicality, not a flat foreign-language imitation',
      highlight: 'natural Italian voices',
    },
    {
      text: 'Correct pronunciation of double consonants, stressed syllables, and accented vowels',
      highlight: 'double consonants, stressed syllables',
    },
    {
      text: 'Reliable handling of Italian academic, artistic, and historical terminology',
      highlight: 'academic, artistic, and historical',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x for shadowing, review, and long-form listening',
      highlight: '0.5x to 4x',
    },
  ],
};

const useCasesData = {
  title: 'How Italian PDF to audio gets used',
  description:
    'Students at Italian universities, art historians, language learners, and professionals producing Italian content.',
  useCases: [
    {
      title: 'Student at an Italian University',
      challenge:
        'Get through dense Italian academic PDFs in fields like law, philosophy, art history, or letteratura.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop assigned PDFs and listen on the train, in the library, or before exams with native Italian.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Broken pronunciation on academic Italian vocabulary makes a 30-page reading sound like noise.',
        },
      ],
    },
    {
      title: 'Italian Learner Building Listening Skill',
      challenge:
        'Move past textbook dialogues and start consuming real Italian PDFs, essays, and short stories.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop any Italian PDF, slow the playback to 0.7x, and shadow native pronunciation with synced highlighting.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Flat, unmusical phrasing trains the wrong rhythm into your ear and slows down real comprehension later.',
        },
      ],
    },
    {
      title: 'Art Historian or Curator',
      challenge:
        'Review Italian-language exhibition catalogs, academic papers, and primary source PDFs without reading every page.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Generate clean Italian audio of long art-historical PDFs, with proper pronunciation of period vocabulary.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Mangles names of artists, schools, and movements, which makes the audio useless for serious research.',
        },
      ],
    },
    {
      title: 'Italian Content Creator',
      challenge:
        'Produce Italian audio versions of articles, newsletters, or course material at consistent quality.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Use the same Italian voice across all output for a recognizable identity, with 24-hour read-along links.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Voices shift in quality or naturalness between sessions, undermining the brand of the content.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Why Italian PDFs Need a Real Italian Voice',
    content:
      'Italian sounds simple on paper — every letter pronounced, no silent vowels, regular spelling. In practice, generic Italian PDF readers still fail because they miss the things that make Italian sound Italian: the length of double consonants, the stress falling on the right syllable, the music of long sentences with embedded clauses. A flat reading of Italian is immediately wrong to any native speaker.',
    contentSecondary:
      'Listening was trained on Italian academic, literary, and journalistic content read by native speakers. Our voices pronounce double consonants as the lengthened sounds they actually are, stress the right syllables in Italian and Latinate vocabulary, and keep the natural cadence of Italian prose. The result is PDF audio you can listen to for a chapter or a paper without an Italian ear flinching every two sentences.',
  },
  {
    title: 'Italian PDF to Audio for Study, Research, and Learning',
    content:
      'For Italian university students and researchers, listening is a way to keep up with the sheer volume of reading expected. Italian academic prose tends to be long and clause-heavy, which is exhausting on the page but actually clearer when you hear it. For Italian learners outside Italy, the converter on this page is the easiest way to hear any Italian PDF you have at the speed you can handle.',
    contentSecondary:
      'The free converter opens a 24-hour read-along link with PDF view, audio, speed controls, and a playbar. The same Italian voices are available in the Listening web app, mobile apps, and Chrome extension, so the language you study at home travels with you for the rest of the day.',
  },
];

const faqs = [
  {
    question: 'Which Italian voices are available?',
    answer:
      'Natural Italian voices in male and female options, trained on real Italian speech with correct musicality and rhythm rather than flat foreign-language imitation.',
  },
  {
    question: 'How are double consonants and stressed vowels handled?',
    answer:
      'Italian doubles (pp, tt, ll, ss) pronounce as the lengthened sounds they actually are, and stress falls on the correct syllable in Italian and Latinate vocabulary.',
  },
  {
    question: 'Can I use this for Italian academic content?',
    answer:
      'Yes. The voices are trained on Italian academic, literary, and journalistic prose. Art history, philosophy, and classical literature pronounce naturally with proper register.',
  },
  {
    question: 'What are the free preview limits?',
    answer:
      'The free preview is capped at 10 pages, 25,000 characters, and 25 MB per PDF. Sign up to convert full Italian documents with no caps.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/italian-pdf-to-audio')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Italian PDF to Audio — Listen to Italian PDFs with Native Voices | Listening',
        description:
          'Convert Italian PDFs to audio with natural Italian AI voices. Designed for academic content, literature, and language learning. Free, no sign-up.',
      }),
    ],
    links: [...hreflangLinks('/italian-pdf-to-audio')],
  }),
  component: () => (
    <PdfToAudioDemoPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription='Quick answers about converting Italian PDFs to audio.'
      faqTitle='Italian PDF to audio FAQ'
      faqs={faqs}
      featureBadge='Italian voices'
      featureFeatures={featureFeatures}
      featureTitle='the Italian PDF tool Italian speakers actually keep using:'
      preferredLang='it'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
